import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { imageService } from '@bedrock/fido-sdk';
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import Icon from '../../../../icon/icon.component';
import { getProgress } from '../../../../progress/progress.utils';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL } from '../../../../signals';
import ImageShader from '../../../../shaders/image.shader';
import {
  defaultContext,
  GAP,
  getFocusedStyle,
  getInitialStyle,
  getUnFocusedStyle,
  ICON_SIZE,
  IMAGE_WIDTH,
} from './epgBox.style';
import { areEPGEntriesEqual } from './epgBox.utils';
import { templates } from './epgBox.constants';

export class EpgBox extends Lightning.Component {
  static _template() {
    return _merge(
      {
        clipping: true,
        Background: {
          rect: true,
          shader: {
            type: ImageShader,
          },
        },
        Image: {
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        Border: {
          rect: true,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
          },
        },
        Container: {
          Time: {},
          Title: {},
          Row: {
            ExtraTitle: {},
            Icon: {
              type: Icon,
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.tag('Title').removeAllListeners('txLoaded');
    this.tag('Time').removeAllListeners('txLoaded');
    this.tag('ExtraTitle').removeAllListeners('txLoaded');
    this.tag('Icon').removeAllListeners('txLoaded');

    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item(value) {
    if (!value) {
      this._item = null;

      return;
    }

    if (areEPGEntriesEqual(this._item ? this._item.content : null, value.content)) {
      this._item = value;

      return;
    }

    this._item = value;

    const { content, itemWidth, itemHeight, isLastItem, offset } = value;
    this._itemWidth = itemWidth;
    this._itemHeight = itemHeight;
    this._currentWidth = itemWidth;
    this._expandedWidth = 0;
    this._innerPaddingRight = isLastItem ? 0 : GAP;
    this._offset = offset;
    // NOTE: this is actually a gap in the EPG Grid's row, however we need the timeline
    // and the grid to be synced in time, so gaps should be inside the boxes
    const w = Math.max(this._itemWidth - this._innerPaddingRight, 1);
    const { screenSize } = this._style;
    const image = content.image ? content.image.id : null;
    const progress = (content.progressBar && content.progressBar.progressValue) || 0;
    const picto = content.pictos ? content.pictos[1] : null;

    this._containerOffset = image ? IMAGE_WIDTH[screenSize] : 0;
    this._texturesToLoad = 0;
    if (content.start.title || content.end.title) {
      this._texturesToLoad += 1;
    }

    if (content.title) {
      this._texturesToLoad += 1;
    }

    if (content.extraTitle) {
      this._texturesToLoad += 1;
    }

    const titleView = this.tag('Title');
    const timeView = this.tag('Time');
    const extraTitleView = this.tag('ExtraTitle');

    const handler = this._handleTextLoading.bind(this);

    titleView.once('txLoaded', handler);
    timeView.once('txLoaded', handler);
    extraTitleView.once('txLoaded', handler);

    if (picto) {
      const icon = this.tag('Icon');
      icon.once('txLoaded', handler);
    }

    this.patch({
      w,
      h: itemHeight,
      Border: {
        w,
        h: itemHeight,
      },
      Background: {
        w,
        h: itemHeight,
        shader: {
          progress: progress ? getProgress(progress) : 0,
        },
      },
      Image: {
        h: itemHeight,
        src: image
          ? imageService.getUrl(image, IMAGE_WIDTH[screenSize], itemHeight, {
              fit: IMAGE_FIT_SCALE_CROP,
              quality: IMAGE_QUALITY,
              format: DEFAULT_IMAGE_FORMAT,
            })
          : null,
      },
      Container: {
        x: GAP + this._containerOffset,
        h: itemHeight,
        Time: {
          visible: Boolean(content.start.title || content.end.title),
          text: {
            text: `${content.start.title} - ${content.end.title}`,
          },
        },
        Title: {
          visible: Boolean(content.title),
          text: {
            text: content.title || '',
          },
        },
        Row: {
          visible: Boolean(content.title || picto),
          ExtraTitle: {
            visible: Boolean(content.title),
            text: {
              text: content.extraTitle || '',
            },
          },
          Icon: {
            visible: Boolean(picto),
            iconData: {
              picto,
              viewBox: {
                w: ICON_SIZE,
                h: ICON_SIZE,
              },
            },
          },
        },
      },
    });
  }

  get rowIndex() {
    return this._rowIndex;
  }

  set rowIndex(value) {
    this._rowIndex = value;
  }

  get itemIndex() {
    return this._itemIndex;
  }

  set itemIndex(value) {
    this._itemIndex = value;
  }

  get itemWidth() {
    return this._itemWidth;
  }

  get collapsedWidth() {
    return this._itemWidth - this._innerPaddingRight;
  }

  get expandedWidth() {
    return this._expandedWidth;
  }

  get offset() {
    return this._offset;
  }

  get currentWidth() {
    return this._currentWidth;
  }

  set currentWidth(value) {
    this.patch({
      w: value,
      Background: { w: value },
      Border: { w: value },
    });
    this._currentWidth = value;
  }

  get padding() {
    return GAP;
  }

  get minInnerOffset() {
    return this.padding + this._containerOffset;
  }

  get innerOffset() {
    return this.tag('Container').x;
  }

  set innerOffset(value) {
    this.tag('Container').x = value;
  }

  _handleTextLoading() {
    this._texturesToLoad -= 1;

    if (this._texturesToLoad > 0) {
      return;
    }

    this._expandedWidth = 0;

    const timeView = this.tag('Time');
    const titleView = this.tag('Title');
    const extraTitleView = this.tag('ExtraTitle');
    const icon = this.tag('Icon');
    const rowView = this.tag('Row');
    const container = this.tag('Container');

    let height = 0;

    if (timeView.visible && timeView.texture && timeView.texture.source) {
      const { w, h } = timeView.texture.source;
      height += h;
      this._expandedWidth = Math.max(this._expandedWidth, w);
    }

    if (titleView.visible && titleView.texture && titleView.texture.source) {
      const { w, h } = titleView.texture.source;
      const padding = height ? 8 : 0;
      titleView.y = height + padding;
      height += h + padding;
      this._expandedWidth = Math.max(this._expandedWidth, w);
    }

    if (rowView.visible) {
      rowView.y = height;

      let h = 0;
      let w = 0;
      if (extraTitleView.visible && extraTitleView.texture && extraTitleView.texture.source) {
        const { w: textWidth, h: textHeight } = extraTitleView.texture.source;
        w += textWidth;
        h = textHeight;
      }

      if (icon.visible) {
        const { w: iconWidth, h: iconHeight } = icon.texture.source;
        w += iconWidth;
        h = Math.max(h, iconHeight);
      }

      this._expandedWidth = Math.max(this._expandedWidth, w);
      height += h;
    }

    container.y = Math.round((this._itemHeight - height) / 2);

    this._expandedWidth += GAP * 2 + this._containerOffset;
  }

  _focus() {
    this._focused = true;
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this._focused = false;
    this.currentWidth = Math.max(1, this.collapsedWidth);
    this.x = this.offset;
    this.patch(getUnFocusedStyle(this._style));
  }
}

EpgBox.templates = templates;
